import React, { memo, useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Bar, getElementAtEvent } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js'
import { useToasts } from 'react-toast-notifications'
import ChartDataLabels from 'chartjs-plugin-datalabels'

import Loader from '../../../../../components/Loader'
import NoData from '../../components/NoData'
import DashboardItemTitle from '../../components/DashboardItemTitle'

import axiosInstance from '../../../../../services/config/AxiosIntance'
import {
  setFilters,
  setTableFilters,
} from '../../../../../store/actions/dashboardActions'

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title)

const ChannelsChart = () => {
  const { addToast } = useToasts()
  const dispatch = useDispatch()
  const { filters } = useSelector((state) => state.dashboard)
  const [loading, setLoading] = useState(false)
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  })

  const chartRef = useRef()

  // Fetch channel data
  useEffect(() => {
    setLoading(true)
    const queryParams = new URLSearchParams(filters).toString()

    axiosInstance
      .get(`/dashboard/channelChart?${queryParams}`)
      .then((res) => {
        const borderColors = res.data.result.labels.map(() => 'black')
        setChartData({
          labels: res.data.result.labels, // keep just the labels (remove % if model still sends it)
          channel_ids: res.data.result.channel_ids,
          datasets: res.data.result.datasets.map((item) => ({
            label: 'Interviews',
            data: item.data,
            backgroundColor: res.data.result.color_codes,
            borderColor: borderColors,
            borderWidth: 1,
          })),
        })
      })
      .catch((err) => {
        addToast(err?.response?.data?.message || 'Internal Server Error', {
          appearance: 'error',
          autoDismiss: true,
        })
      })
      .finally(() => setLoading(false))
  }, [filters.dates, filters.job_id, filters.company_id])

  // Highlight selected bar
  useEffect(() => {
    if (chartData.datasets.length) {
      const selectedIndex = chartData.channel_ids?.findIndex(
        (item) => item === parseInt(filters.channel_id)
      )
      const selected = chartData.datasets[0].borderColor.map((color, index) =>
        index === selectedIndex ? 'red' : 'black'
      )
      const updatedDataset = [
        {
          ...chartData.datasets[0],
          borderColor: selected,
        },
      ]
      setChartData((prev) => ({ ...prev, datasets: updatedDataset }))
    }
  }, [filters.channel_id])

  // Handle bar click
  const handleBarClick = (e) => {
    const event = getElementAtEvent(chartRef.current, e)
    if (event.length) {
      const index = event[0].index
      const channelId = chartData.channel_ids[index]
      const channelName = chartData.labels[index]
      dispatch(setFilters({ channel_id: channelId }))
      dispatch(setTableFilters({ channel: channelName }))
    }
  }

  // Bar chart options
  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      title: {
        color: 'black',
        display: true,
        align: 'start',
        position: 'top',
        text: 'Channels',
        font: {
          size: 24,
          family: "'Lato', 'sans-serif'",
          weight: 300,
        },
      },
      legend: { display: false },
      datalabels: {
        color: 'black',
        anchor: 'end',
        align: 'top',
        formatter: (value) => value, // show actual counts
      },
    },
    scales: {
      y: {
        grid: {
          display: false,
        },
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: { color: '#000' },
      },
    },
  }

  // Render chart
  const component = loading ? (
    <Loader />
  ) : chartData.labels.length ? (
    <Bar
      ref={chartRef}
      data={chartData}
      options={options}
      height={100}
      plugins={[ChartDataLabels]}
      onClick={handleBarClick}
    />
  ) : (
    <>
      <DashboardItemTitle title="Channels" />
      <NoData />
    </>
  )

  return component
}

export default memo(ChannelsChart)
